import React, { useRef, useState } from 'react';

// % recobro per enfunde week: one bar per week that has an enfunde count, a dashed line at
// 100%, and the week's cinta as a swatch under its label. Single series, so no legend; the
// exact numbers are in the hover tooltip and in the table below the chart.
const H = 260;          // plot height
const PAD = { top: 16, right: 16, bottom: 46, left: 44 };
const BAR = 22;         // bar width
const GAP = 10;         // space between bars
const TIP_HALF = 120;   // a bit over half the tooltip's width, to keep it inside the chart box

const pct = (v) => `${(v * 100).toLocaleString('es-EC', { maximumFractionDigits: 1 })}%`;
const int = (v) => Number(v).toLocaleString('es-EC');

export default function RecobroChart({ rows }) {
  const [hover, setHover] = useState(null); // { row, x }
  const boxRef = useRef(null);
  const bars = rows.filter((r) => r.recovery != null);
  if (bars.length === 0) {
    return <div className="recobro-chart-empty">Aún no hay semanas con enfunde y cosecha para graficar.</div>;
  }

  // Axis top: at least 125%, always on a labelled 25% step, with headroom above the tallest bar.
  const top = Math.max(1.25, Math.ceil((Math.max(...bars.map((r) => r.recovery)) + 0.05) * 4) / 4);
  const width = PAD.left + PAD.right + bars.length * (BAR + GAP);
  const y = (v) => PAD.top + H - (v / top) * H;
  const ticks = [];
  for (let t = 0; t <= top + 1e-9; t += 0.25) ticks.push(t);

  return (
    <div className="recobro-chart" ref={boxRef} onMouseLeave={() => setHover(null)}>
      <svg
        width={width}
        height={PAD.top + H + PAD.bottom}
        role="img"
        aria-label="Porcentaje de recobro por semana de enfunde"
      >
        {ticks.map((t) => (
          <g key={t}>
            <line x1={PAD.left} x2={width - PAD.right} y1={y(t)} y2={y(t)} className="recobro-grid" />
            <text x={PAD.left - 6} y={y(t)} className="recobro-axis" textAnchor="end" dominantBaseline="middle">
              {Math.round(t * 100)}%
            </text>
          </g>
        ))}
        <line
          x1={PAD.left}
          x2={width - PAD.right}
          y1={y(1)}
          y2={y(1)}
          className="recobro-target"
        />
        {bars.map((r, i) => {
          const x = PAD.left + GAP / 2 + i * (BAR + GAP);
          // At least 4px tall so the rounded top always draws (a 0% week shows as a sliver).
          const barTop = Math.min(y(r.recovery), PAD.top + H - 4);
          const active = hover?.row.key === r.key;
          return (
            <g key={r.key}>
              {/* Rounded top, square base (bar anchored to the baseline). */}
              <path
                d={`M${x},${PAD.top + H} V${barTop + 4} Q${x},${barTop} ${x + 4},${barTop} H${x + BAR - 4} Q${x + BAR},${barTop} ${x + BAR},${barTop + 4} V${PAD.top + H} Z`}
                className={`recobro-bar${active ? ' active' : ''}`}
              />
              <circle
                cx={x + BAR / 2}
                cy={PAD.top + H + 12}
                r={5}
                fill={r.ribbon_hex_color || '#ffffff'}
                className="recobro-chart-swatch"
              />
              <text x={x + BAR / 2} y={PAD.top + H + 32} className="recobro-axis" textAnchor="middle">
                {r.enfunde_week}
              </text>
              {/* Hit target taller and wider than the bar. */}
              <rect
                x={x - GAP / 2}
                y={PAD.top}
                width={BAR + GAP}
                height={H + PAD.bottom}
                fill="transparent"
                onMouseEnter={() => setHover({ row: r, x: x + BAR / 2 })}
              />
            </g>
          );
        })}
      </svg>
      {hover && (
        <div
          className="recobro-tooltip"
          // Pinned near the top of the plot and kept inside it, so it never gets clipped.
          style={{
            left: Math.min(
              Math.max(hover.x, TIP_HALF + (boxRef.current?.scrollLeft || 0)),
              (boxRef.current?.scrollLeft || 0) + (boxRef.current?.clientWidth || width) - TIP_HALF,
            ),
            top: 6,
          }}
          role="tooltip"
        >
          <div className="recobro-tooltip-title">
            <span className="recobro-tooltip-swatch" style={{ background: hover.row.ribbon_hex_color || '#fff' }} />
            Semana {hover.row.enfunde_week} de {hover.row.enfunde_year} · {hover.row.ribbon_color || 'sin cinta'}
          </div>
          <div>Enfundado: <strong>{int(hover.row.enfunde)}</strong></div>
          <div>Cosechado: <strong>{int(hover.row.harvested)}</strong></div>
          <div>Saldo: <strong>{int(hover.row.saldo)}</strong></div>
          <div>Recobro: <strong>{pct(hover.row.recovery)}</strong></div>
        </div>
      )}
    </div>
  );
}
